import { webBuildInfo } from '@/shared/config/build-info';
import {
  CACHE_BUST_PARAM,
  reloadWithCacheBust,
} from '@/shared/lib/reload-with-cache-bust';

/**
 * Detect a stale SPA shell after deploy (XT-413).
 *
 * Fetches `/build-id.json` with `cache: 'no-store'` and a query cache-buster —
 * `no-store` only defeats the *browser* cache, and the shared cache that serves
 * the stale shell keys on the query string, not on request headers. If the live
 * id differs from the SHA baked into this bundle, the document is stale → one
 * hard reload (cooldown-guarded).
 *
 * Host-agnostic: always same-origin `/build-id.json`.
 *
 * This probe must never block mounting. It sits in front of `mount()` in
 * `index.tsx`, so an unbounded fetch here is a blank screen with a perfectly
 * healthy bundle on any slow or filtered network — hence the hard timeout.
 */

const SHELL_RELOAD_KEY = 'tp:shell-mismatch-reload-at';
/** Avoid reload storms if build-id stays ahead of a sticky HTML edge. */
const SHELL_RELOAD_COOLDOWN_MS = 5 * 60 * 1000;
/** Upper bound on how long boot may wait for the build-id probe. */
const BUILD_ID_TIMEOUT_MS = 2_500;

export type FreshShellResult = 'ok' | 'reloading' | 'skipped';

type BuildIdFile = { sha?: string };

const looksLikeHtml = (contentType: string | null, body: string): boolean => {
  const ct = (contentType || '').toLowerCase();
  if (ct.includes('text/html')) return true;
  const head = body.trimStart().slice(0, 32).toLowerCase();
  return head.startsWith('<!doctype html') || head.startsWith('<html');
};

export const shouldReloadForBuildMismatch = (
  liveSha: string | undefined,
  bundleSha: string,
  now: number,
  lastReloadAt: number | null
): boolean => {
  if (!liveSha || liveSha === 'dev' || bundleSha === 'dev') return false;
  if (liveSha === bundleSha) return false;
  if (lastReloadAt !== null && now - lastReloadAt < SHELL_RELOAD_COOLDOWN_MS) {
    return false;
  }
  return true;
};

const readLastReloadAt = (): number | null => {
  try {
    const raw = sessionStorage.getItem(SHELL_RELOAD_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
};

const stampReload = (now: number): void => {
  try {
    sessionStorage.setItem(SHELL_RELOAD_KEY, String(now));
  } catch {
    // ignore
  }
};

/**
 * Call once before mounting React. Safe to no-op on failure.
 */
export const ensureFreshShell = async (): Promise<FreshShellResult> => {
  // Local Vite dev has no dist/build-id.json — skip.
  if (import.meta.env.DEV) return 'skipped';

  try {
    const res = await fetch(
      `/build-id.json?${CACHE_BUST_PARAM}=${Date.now()}`,
      {
        cache: 'no-store',
        credentials: 'same-origin',
        signal: AbortSignal.timeout(BUILD_ID_TIMEOUT_MS),
      }
    );
    if (!res.ok) return 'ok';

    const text = await res.text();
    if (looksLikeHtml(res.headers.get('content-type'), text)) {
      // Catch-all served HTML for a missing file — cannot trust.
      return 'ok';
    }

    let data: BuildIdFile;
    try {
      data = JSON.parse(text) as BuildIdFile;
    } catch {
      return 'ok';
    }

    const now = Date.now();
    if (
      !shouldReloadForBuildMismatch(
        data.sha,
        webBuildInfo.sha,
        now,
        readLastReloadAt()
      )
    ) {
      return 'ok';
    }

    stampReload(now);
    reloadWithCacheBust();
    return 'reloading';
  } catch {
    // Includes the timeout abort. Never block the app on this probe.
    return 'ok';
  }
};
